import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Heart, MessageSquare, User, LogOut, Menu, X, ChevronDown, Crown, CreditCard, 
  HandHeart, Home, Info, Shield, FileText, Mail, Search, LayoutDashboard, Users,
  Settings, Star, Bell, Calendar, MapPin, Image, Gift, Bookmark, HelpCircle,
  Link as LinkIcon, ShoppingBag
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Icon mapping for dynamic rendering
const ICON_MAP = {
  Search, Heart, MessageSquare, User, Settings, Crown, CreditCard, HandHeart,
  Info, HelpCircle, Shield, FileText, Mail, Bookmark, Home, Star, Bell,
  Calendar, MapPin, Image, Gift, Users, Link: LinkIcon, LayoutDashboard, ShoppingBag
};

const getIconComponent = (iconName) => {
  return ICON_MAP[iconName] || LinkIcon;
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [interestCount, setInterestCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [mainNav, setMainNav] = useState([]);
  const [moreMenu, setMoreMenu] = useState([]);
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuth();

  // Check if user has verification badge (for display purposes)
  const hasVerificationBadge = user?.isVerified === true;
  
  // Check if user account is approved (can access the app)
  // For backward compatibility: treat undefined/null as approved for legacy users with firstName
  const isAccountApproved = user?.accountApproved === true || 
    (user?.firstName && (user?.accountApproved === undefined || user?.accountApproved === null));

  // Fetch navigation menu settings
  useEffect(() => {
    const fetchNavSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/navigation-menu/public`);
        if (response.data) {
          if (response.data.main_nav?.length > 0) {
            setMainNav(response.data.main_nav.filter(item => item.enabled).sort((a, b) => a.order - b.order));
          }
          if (response.data.more_menu?.length > 0) {
            setMoreMenu(response.data.more_menu.filter(item => item.enabled).sort((a, b) => a.order - b.order));
          }
        }
      } catch (error) {
        console.error('Failed to fetch navigation settings:', error);
      }
    };
    fetchNavSettings();
  }, []);

  // Fetch unread message count and interest count
  useEffect(() => {
    if (!isAuthenticated || !isAccountApproved) {
      setUnreadCount(0);
      setInterestCount(0);
      setIsAdmin(false);
      setIsPremium(false);
      return;
    }

    // Check if user is admin and premium
    if (user?.isAdmin) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
    
    if (user?.isPremium) {
      setIsPremium(true);
    } else {
      setIsPremium(false);
    }

    const fetchCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const [messagesRes, interestsRes] = await Promise.all([
          axios.get(`${API_URL}/api/messages/unread-count`, {
            headers: { 'Authorization': `Bearer ${token}` },
            withCredentials: true
          }),
          axios.get(`${API_URL}/api/interests/count/received`, {
            headers: { 'Authorization': `Bearer ${token}` },
            withCredentials: true
          })
        ]);
        setUnreadCount(messagesRes.data.unreadCount || 0);
        setInterestCount(interestsRes.data.count || 0);
      } catch (error) {
        console.error('Failed to fetch counts:', error);
      }
    };

    fetchCounts();
    // Poll for counts every 30 seconds
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isAccountApproved, user]);

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav 
      className="bg-white shadow-md fixed top-0 left-0 right-0 z-50"
    >
      <div className="w-full px-2">
        <div className="flex justify-between items-center h-16">
          {/* Logo - only link to home if account approved OR not authenticated */}
          {(isAccountApproved || !isAuthenticated) ? (
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_secureuser-reg/artifacts/wk6fs92i_487AAA30-7E16-4977-B648-18FE5729A675.jpg" 
                alt="qbool" 
                className="h-14 w-auto"
              />
            </Link>
          ) : (
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_secureuser-reg/artifacts/wk6fs92i_487AAA30-7E16-4977-B648-18FE5729A675.jpg" 
                alt="qbool" 
                className="h-14 w-auto cursor-default"
              />
            </div>
          )}

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {isAuthenticated && isAccountApproved && (
              <>
                {/* Dynamic Main Nav Items */}
                {mainNav.length > 0 ? (
                  mainNav.map(item => {
                    const IconComponent = getIconComponent(item.icon);
                    const iconStyle = item.iconColor ? { color: item.iconColor } : {};
                    const isInterests = item.url === '/interests';
                    const isMessages = item.url === '/messages';
                    const badgeCount = isInterests ? interestCount : (isMessages ? unreadCount : 0);
                    
                    return (
                      <Link 
                        key={item.id} 
                        to={item.url} 
                        className="flex items-center text-gray-700 hover:text-gray-900 transition-colors relative"
                      >
                        <IconComponent className="w-5 h-5 mr-1" style={iconStyle} />
                        {item.label}
                        {item.showBadge && badgeCount > 0 && (
                          <span 
                            className="absolute -top-1 -right-2 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold"
                            style={{ backgroundColor: 'var(--color-primary)' }}
                          >
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        )}
                      </Link>
                    );
                  })
                ) : (
                  // Fallback if no nav settings loaded
                  <>
                    <Link 
                      to="/search" 
                      className="text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      Search Matches
                    </Link>
                    <Link 
                      to="/shortlist" 
                      className="text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      Shortlist
                    </Link>
                    <Link to="/interests" className="flex items-center text-gray-700 hover:text-gray-900 transition-colors relative">
                      <Heart className="w-5 h-5 mr-1 text-coral-500" />
                      Interests
                      {interestCount > 0 && (
                        <span className="absolute -top-1 -right-2 bg-coral-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                          {interestCount > 99 ? '99+' : interestCount}
                        </span>
                      )}
                    </Link>
                    <Link to="/messages" className="flex items-center text-gray-700 hover:text-gray-900 transition-colors relative">
                      <MessageSquare className="w-5 h-5 mr-1" />
                      Messages
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-2 bg-mint-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Link>
                  </>
                )}

                {/* More Menu Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center text-gray-700 hover:text-gray-900 transition-colors focus:outline-none">
                    More <ChevronDown className="w-4 h-4 ml-1" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Admin Section - Only for admins */}
                    {isAdmin && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="cursor-pointer flex items-center bg-purple-50 hover:bg-purple-100">
                            <LayoutDashboard className="w-4 h-4 mr-2 text-purple-600" />
                            <span className="font-semibold text-purple-600">Admin Dashboard</span>
                          </Link>
                        </DropdownMenuItem>
                        <div className="h-px bg-gray-200 my-1"></div>
                      </>
                    )}
                    
                    {/* Shop Link */}
                    <DropdownMenuItem asChild>
                      <a href="https://qboolgiftshop.square.site/" target="_blank" rel="noopener noreferrer" className="cursor-pointer flex items-center">
                        <ShoppingBag className="w-4 h-4 mr-2 text-pink-500" />
                        Shop
                      </a>
                    </DropdownMenuItem>
                    
                    {/* Dynamic More Menu Items */}
                    {moreMenu.length > 0 ? (
                      moreMenu.map(item => {
                        const IconComponent = getIconComponent(item.icon);
                        const iconStyle = item.iconColor ? { color: item.iconColor } : {};
                        return (
                          <DropdownMenuItem key={item.id} asChild>
                            <Link to={item.url} className="cursor-pointer flex items-center">
                              <IconComponent className="w-4 h-4 mr-2" style={iconStyle} />
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })
                    ) : (
                      // Fallback menu items
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/profile" className="cursor-pointer flex items-center">
                            <User className="w-4 h-4 mr-2" />
                            My Profile
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/account-settings" className="cursor-pointer flex items-center">
                            <User className="w-4 h-4 mr-2" />
                            My Account
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/pricing" className="cursor-pointer flex items-center">
                            <Crown className="w-4 h-4 mr-2 text-yellow-500" />
                            Premium
                          </Link>
                        </DropdownMenuItem>
                        <div className="h-px bg-gray-200 my-1"></div>
                        <DropdownMenuItem asChild>
                          <Link to="/" className="cursor-pointer flex items-center">
                            <Home className="w-4 h-4 mr-2" />
                            Home
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/about" className="cursor-pointer flex items-center">
                            <Info className="w-4 h-4 mr-2" />
                            About Us
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {/* Divider */}
                    <div className="h-px bg-gray-200 my-1"></div>
                    
                    {/* Logout */}
                    <DropdownMenuItem asChild>
                      <button onClick={handleLogout} className="cursor-pointer flex items-center w-full text-red-600">
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </button>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Premium Badge - Desktop */}
                {isPremium ? (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="flex items-center gap-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-3 py-1.5 rounded-full shadow-md hover:from-yellow-500 hover:to-yellow-700 transition-all"
                  >
                    <Crown className="w-4 h-4" />
                    <span className="text-sm font-semibold">Premium</span>
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="flex items-center gap-1 bg-gradient-to-r from-red-600 to-pink-600 text-white px-3 py-1.5 rounded-full shadow-md hover:from-red-700 hover:to-pink-700 transition-all"
                  >
                    <Crown className="w-4 h-4" />
                    <span className="text-sm font-semibold">Upgrade</span>
                  </button>
                )}
              </>
            )}

            {/* Logout for authenticated but account not approved users */}
            {isAuthenticated && !isAccountApproved && (
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            )}

            {!isAuthenticated && (
              <>
                <a href="https://qboolgiftshop.square.site/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-gray-700 hover:text-pink-500 transition-colors" data-testid="shop-nav-link">
                  <ShoppingBag className="w-5 h-5" />
                  <span>Shop</span>
                </a>
                <Link to="/login" className="text-gray-700 hover:text-gray-900 transition-colors">
                  Login
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button & Premium Badge */}
          {isAuthenticated && isAccountApproved && (
            <div className="md:hidden flex items-center gap-2">
              {/* Premium Badge - Mobile (Right Side) */}
              {isPremium ? (
                <button
                  onClick={() => navigate('/pricing')}
                  className="flex items-center gap-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-2 py-1 rounded-full shadow-sm hover:from-yellow-500 hover:to-yellow-700 transition-all"
                >
                  <Crown className="w-3 h-3" />
                  <span className="text-xs font-semibold">Premium</span>
                </button>
              ) : (
                <button
                  onClick={() => navigate('/pricing')}
                  className="flex items-center gap-1 bg-gradient-to-r from-red-600 to-pink-600 text-white px-2 py-1 rounded-full shadow-sm hover:from-red-700 hover:to-pink-700 transition-all"
                >
                  <Crown className="w-3 h-3" />
                  <span className="text-xs font-semibold">Upgrade</span>
                </button>
              )}
              
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-gray-900"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          )}

          {/* Mobile button for authenticated but account not approved users */}
          {isAuthenticated && !isAccountApproved && (
            <div className="md:hidden">
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          )}

          {/* Mobile buttons for non-authenticated users */}
          {!isAuthenticated && (
            <div className="md:hidden flex items-center gap-2">
              <a href="https://qboolgiftshop.square.site/" target="_blank" rel="noopener noreferrer" data-testid="shop-nav-mobile">
                <Button variant="ghost" size="sm" className="text-pink-500 hover:text-pink-600 px-2">
                  <ShoppingBag className="w-5 h-5" />
                </Button>
              </a>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-gray-700 hover:text-gray-900">
                  Login
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && isAuthenticated && isAccountApproved && (
        <div className="md:hidden bg-white border-t fixed left-0 right-0 top-16 bottom-0 overflow-y-auto z-40">
          <div className="px-4 pt-2 pb-4 space-y-2">
            {/* Dynamic Main Nav Items for Mobile */}
            {mainNav.length > 0 ? (
              mainNav.map(item => {
                const IconComponent = getIconComponent(item.icon);
                const iconStyle = item.iconColor ? { color: item.iconColor } : {};
                const isInterests = item.url === '/interests';
                const isMessages = item.url === '/messages';
                const badgeCount = isInterests ? interestCount : (isMessages ? unreadCount : 0);
                
                return (
                  <Link 
                    key={item.id}
                    to={item.url} 
                    className="flex items-center justify-between py-2 text-gray-700 hover:text-gray-900" 
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="flex items-center">
                      <IconComponent className="w-5 h-5 mr-2" style={iconStyle} />
                      <span>{item.label}</span>
                    </div>
                    {item.showBadge && badgeCount > 0 && (
                      <span className={`${isInterests ? 'bg-coral-500' : 'bg-mint-500'} text-white text-xs rounded-full px-2 py-1 font-semibold`}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </Link>
                );
              })
            ) : (
              // Fallback mobile nav
              <>
                <Link to="/search" className="flex items-center py-2 text-gray-700 hover:text-gray-900" onClick={() => setIsMenuOpen(false)}>
                  <Search className="w-5 h-5 mr-2" />
                  Search Matches
                </Link>
                <Link to="/shortlist" className="flex items-center py-2 text-gray-700 hover:text-gray-900" onClick={() => setIsMenuOpen(false)}>
                  <Heart className="w-5 h-5 mr-2 text-coral-500" />
                  Shortlist
                </Link>
                <Link to="/interests" className="flex items-center justify-between py-2 text-gray-700 hover:text-gray-900" onClick={() => setIsMenuOpen(false)}>
                  <div className="flex items-center">
                    <Heart className="w-5 h-5 mr-2 text-coral-500" />
                    <span>Interests</span>
                  </div>
                  {interestCount > 0 && (
                    <span className="bg-coral-500 text-white text-xs rounded-full px-2 py-1 font-semibold">
                      {interestCount > 99 ? '99+' : interestCount}
                    </span>
                  )}
                </Link>
                <Link to="/messages" className="flex items-center justify-between py-2 text-gray-700 hover:text-gray-900" onClick={() => setIsMenuOpen(false)}>
                  <div className="flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2" />
                    <span>Messages</span>
                  </div>
                  {unreadCount > 0 && (
                    <span className="bg-mint-500 text-white text-xs rounded-full px-2 py-1 font-semibold">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              </>
            )}
            
            {/* More Menu Section in Mobile */}
            <div className="border-t border-gray-200 my-2 pt-2">
              <p className="text-xs text-gray-500 uppercase px-2 mb-2">More</p>
              {moreMenu.length > 0 ? (
                moreMenu.map(item => {
                  const IconComponent = getIconComponent(item.icon);
                  const iconStyle = item.iconColor ? { color: item.iconColor } : {};
                  return (
                    <Link 
                      key={item.id}
                      to={item.url} 
                      className="flex items-center py-2 text-gray-700 hover:text-gray-900" 
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <IconComponent className="w-5 h-5 mr-2" style={iconStyle} />
                      {item.label}
                    </Link>
                  );
                })
              ) : (
                // Fallback more menu
                <>
                  <Link to="/profile" className="flex items-center py-2 text-gray-700 hover:text-gray-900" onClick={() => setIsMenuOpen(false)}>
                    <User className="w-5 h-5 mr-2" />
                    My Profile
                  </Link>
                  <Link to="/account-settings" className="flex items-center py-2 text-gray-700 hover:text-gray-900" onClick={() => setIsMenuOpen(false)}>
                    <User className="w-5 h-5 mr-2" />
                    My Account
                  </Link>
                  <Link to="/pricing" className="flex items-center py-2 text-gray-700 hover:text-gray-900" onClick={() => setIsMenuOpen(false)}>
                    <Crown className="w-5 h-5 mr-2 text-yellow-500" />
                    Premium
                  </Link>
                  <Link to="/" className="flex items-center py-2 text-gray-700 hover:text-gray-900" onClick={() => setIsMenuOpen(false)}>
                    <Home className="w-5 h-5 mr-2" />
                    Home
                  </Link>
                  <Link to="/about" className="flex items-center py-2 text-gray-700 hover:text-gray-900" onClick={() => setIsMenuOpen(false)}>
                    <Info className="w-5 h-5 mr-2" />
                    About Us
                  </Link>
                </>
              )}
              
              {/* Shop + Admin + Logout in Mobile */}
              <div className="border-t border-gray-200 mt-2 pt-2">
                <a href="https://qboolgiftshop.square.site/" target="_blank" rel="noopener noreferrer" className="flex items-center py-2 text-gray-700 hover:text-gray-900" onClick={() => setIsMenuOpen(false)}>
                  <ShoppingBag className="w-5 h-5 mr-2 text-pink-500" />
                  Shop
                </a>
                {isAdmin && (
                  <Link to="/admin" className="flex items-center py-2 font-semibold text-purple-600" onClick={() => setIsMenuOpen(false)}>
                    <LayoutDashboard className="w-5 h-5 mr-2" />
                    Admin Dashboard
                  </Link>
                )}
                <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="w-full text-left py-2 text-red-600 hover:text-red-700 flex items-center font-semibold">
                  <LogOut className="w-5 h-5 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;